// "Portfolio preview" block (showcase {page, sec, h, x, btn, icon}): a card that introduces one portfolio section and shows its latest
// work, with a button to the section. Photography shows photo tiles, video shows video cards, every study-based template
// (UX, blog, blank...) shows plain study cards. Renders nothing when the section is gone or hidden.
(function () {
  const { Box, Typography } = MaterialUI;
  const { useState, useEffect } = React;

  const COUNT = { photography: 6, video: 3 };
  const hrefOf = (page, sec, slug) => '#/' + [...(page === 'about' ? ['portfolio'] : ['p', page]), sec, slug].filter(Boolean).join('/');
  const go = href => { window.location.hash = href; };
  // A study's picture: its card thumbnail, else its cover, else its first image (also inside grids)
  const firstImage = blocks => {
    for (const b of blocks || []) {
      if ((b.t === 'img' || b.t === 'media') && b.u) return b.u;
      if (b.t === 'grid') { const u = firstImage(b.items); if (u) return u; }
    }
    return null;
  };

  async function load(page, sec) {
    const api = window.PortfolioApi;
    const section = (await api.sections.list(page)).sections.find(s => s.key === sec && s.published !== false);
    if (!section) return null;
    const t = section.template;
    const visible = list => list.filter(i => i.published !== false).slice(0, COUNT[t] || 3);
    if (t === 'photography') return { section, photos: visible((await api.photos.list(sec)).items) };
    if (t === 'video') return { section, videos: visible((await api.videos.list(sec)).items) };
    return { section, studies: visible((await api.list(sec)).studies) };
  }

  function PhotoTiles({ photos, href }) {
    const { Card } = window.Components;
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', '@container (min-width: 620px)': { gridTemplateColumns: 'repeat(6, minmax(0, 1fr))' }, gap: 1 }}>
        {photos.map(p => (
          <Card key={p.id} variant="filled" clickable onClick={() => go(href)} aria-label={p.title || 'Open photography'} sx={{ aspectRatio: '1 / 1', borderRadius: 'var(--m3-shape-medium)' }}>
            {/* sizes auto (lazy images): the browser picks the 480/800/1200px copy that fits the tile, not the 1600px one */}
            <Box component="img" {...window.Components.responsiveImg(p.url || p.thumbUrl, 'auto, (max-width: 900px) 34vw, 200px')} alt={p.title || ''} loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </Card>
        ))}
      </Box>
    );
  }

  function VideoCards({ videos, href }) {
    const { Card, GoogleIcon } = window.Components;
    const { has } = window.useConsent(); // thumbnails come from YouTube, so they wait for the embedded-media consent too
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', '@container (min-width: 620px)': { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
        {videos.map(v => (
          <Card key={v.id} variant="outlined" clickable onClick={() => go(href)}>
            <Box sx={{ aspectRatio: '16 / 9', display: 'grid', placeItems: 'center', backgroundColor: 'var(--m3-surface-container-highest)', color: 'var(--m3-on-surface-variant)', ...(has('media') && v.youtubeId ? { backgroundImage: `url(https://i.ytimg.com/vi/${v.youtubeId}/hqdefault.jpg)`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}) }}>
              {!(has('media') && v.youtubeId) && <GoogleIcon name="play_circle" size={36} />}
            </Box>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle1" component="h4">{v.title}</Typography>
              {v.category && <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>{v.category}</Typography>}
            </Box>
          </Card>
        ))}
      </Box>
    );
  }

  function StudyCards({ studies, page, sec }) {
    const { Card } = window.Components;
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', '@container (min-width: 620px)': { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
        {studies.map(s => {
          const img = s.thumb || s.cover || firstImage(s.blocks); // the page's card thumbnail first
          return (
            <Card key={s.slug} variant="outlined" clickable onClick={() => go(hrefOf(page, sec, s.slug))}>
              {img && <Box component="img" src={img} alt="" loading="lazy" sx={{ width: '100%', aspectRatio: '16 / 9', objectFit: s.thumb && s.thumbFit === 'contain' ? 'contain' : 'cover', display: 'block', backgroundColor: 'var(--m3-surface-container-highest)' }} />}
              <Box sx={{ p: 2 }}>
                {s.kind && <Typography variant="overline" component="p" sx={{ color: 'var(--m3-primary)', mb: 0.5 }}>{s.kind}</Typography>}
                <Typography variant="subtitle1" component="h4">{s.title.split(' —')[0]}</Typography>
                {s.description && <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{s.description}</Typography>}
              </Box>
            </Card>
          );
        })}
      </Box>
    );
  }

  function SectionShowcase({ b }) {
    const { FeatureCard } = window.Components;
    const [data, setData] = useState(undefined); // undefined while loading, null when the section is not shown
    useEffect(() => {
      let on = true;
      if (!b.page || !b.sec) { setData(null); return; }
      load(b.page, b.sec).then(d => on && setData(d)).catch(() => on && setData(null));
      return () => { on = false; };
    }, [b.page, b.sec]);
    if (!data) return data === undefined ? <Box sx={{ minHeight: 240, my: 3 }} /> : null;
    const href = hrefOf(b.page, b.sec);
    const { section } = data;

    return (
      <FeatureCard icon={b.icon || section.icon} title={b.h || section.title} text={b.x} action={{ label: b.btn || `Explore ${section.title}`, href }}>
        {data.photos && data.photos.length > 0 && <PhotoTiles photos={data.photos} href={href} />}
        {data.videos && data.videos.length > 0 && <VideoCards videos={data.videos} href={href} />}
        {data.studies && data.studies.length > 0 && <StudyCards studies={data.studies} page={b.page} sec={b.sec} />}
      </FeatureCard>
    );
  }

  window.Components = window.Components || {};
  window.Components.SectionShowcase = SectionShowcase;
  window.Components.PhotoTiles = PhotoTiles; // photos: [{ id, url, thumbUrl, title }], href: where a click goes
})();
